import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, MailX, CheckCircle2, AlertTriangle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>("loading");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    const validate = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(
          `${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${token}`,
          { headers: { apikey: anonKey } }
        );
        const data = await res.json();
        if (data.valid === true) setStatus("valid");
        else if (data.reason === "already_unsubscribed") setStatus("already");
        else setStatus("invalid");
      } catch {
        setStatus("invalid");
      }
    };

    validate();
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) throw error;
      if (data?.success) setStatus("success");
      else if (data?.reason === "already_unsubscribed") setStatus("already");
      else setStatus("error");
    } catch {
      setStatus("error");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="container max-w-md py-24 px-4 text-center space-y-6">
        {status === "loading" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Se verifică...</p>
          </>
        )}

        {status === "valid" && (
          <>
            <MailX className="h-12 w-12 text-primary mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Dezabonare</h1>
            <p className="text-muted-foreground">
              Ești sigur că vrei să te dezabonezi de la emailurile EZ Factura? Nu vei mai primi notificări prin email.
            </p>
            <Button
              onClick={handleUnsubscribe}
              disabled={processing}
              className="w-full h-12 font-semibold"
            >
              {processing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirmă dezabonarea
            </Button>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-success mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Te-ai dezabonat</h1>
            <p className="text-muted-foreground">
              Nu vei mai primi emailuri de la EZ Factura. Poți continua să folosești platforma normal.
            </p>
          </>
        )}

        {status === "already" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Deja dezabonat</h1>
            <p className="text-muted-foreground">
              Această adresă de email este deja dezabonată.
            </p>
          </>
        )}

        {status === "invalid" && (
          <>
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Link invalid</h1>
            <p className="text-muted-foreground">
              Acest link de dezabonare este invalid sau a expirat.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Eroare</h1>
            <p className="text-muted-foreground">
              A apărut o eroare. Te rugăm să încerci din nou sau să ne contactezi la contact@ezfactura.ro.
            </p>
          </>
        )}
      </main>
      <AppFooter />
    </div>
  );
};

export default Unsubscribe;
